import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import api, { OrdersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiMail, FiPhone, FiCopy, FiX, FiSearch, FiMapPin, FiChevronLeft, FiChevronRight, FiExternalLink, FiFileText, FiPackage, FiClipboard } from 'react-icons/fi';
import TaskDetailsModal from './Tasks/components/Modals/TaskDetailsModal';

// Design tokens
const colors = {
  // Warm neutrals
  stone50: '#fafaf9',
  stone100: '#f5f5f4',
  stone200: '#e7e5e4',
  stone300: '#d6d3d1',
  stone400: '#a8a29e',
  stone500: '#78716c',
  stone600: '#57534e',
  stone700: '#44403c',
  stone800: '#292524',
  stone900: '#1c1917',
  // Accent - teal/cyan
  accent50: '#ecfeff',
  accent100: '#cffafe',
  accent200: '#a5f3fc',
  accent500: '#06b6d4',
  accent600: '#0891b2',
  accent700: '#0e7490',
  // Status colors
  emerald50: '#ecfdf5',
  emerald600: '#059669',
  emerald700: '#047857',
  amber50: '#fffbeb',
  amber600: '#d97706',
  amber700: '#b45309',
  rose50: '#fff1f2',
  rose600: '#e11d48',
  rose700: '#be123c',
  blue50: '#eff6ff',
  blue600: '#2563eb',
  blue700: '#1d4ed8',
  violet50: '#f5f3ff',
  violet600: '#7c3aed',
};

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`,
    padding: '24px 32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: colors.stone800,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 14,
    color: colors.stone500,
    margin: '4px 0 0',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${colors.stone200}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
    marginBottom: 20,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.stone100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardBody: {
    padding: '20px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: colors.stone500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  input: {
    padding: '8px 12px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: colors.stone50,
    color: colors.stone800,
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    padding: '4px',
    background: colors.stone100,
    borderRadius: 12,
    marginBottom: 5,
    marginTop: 4,
  },
  tab: {
    flex: 1,
    padding: '10px 14px',
    fontSize: 12,
    fontWeight: 500,
    color: colors.stone500,
    background: 'transparent',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  tabActive: {
    background: '#fff',
    color: colors.stone800,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    maxWidth: 320,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.stone400,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px 8px 36px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone800,
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  select: {
    padding: '8px 28px 8px 12px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone700,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: 12,
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 11,
    fontWeight: 600,
    color: colors.stone500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: colors.stone50,
    borderBottom: `1px solid ${colors.stone200}`,
  },
  td: {
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.stone100}`,
    color: colors.stone700,
  },
  // Match button styling used in Invoices.js
  btnPrimary: {
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: colors.stone800,
    background: '#fff',
    border: `1.5px solid ${colors.stone300}`,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: 'none',
  },
  btnSecondary: {
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 500,
    color: colors.stone700,
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  btnIcon: {
    padding: '8px',
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    color: colors.stone500,
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkBtn: {
    padding: 0,
    background: 'transparent',
    border: 'none',
    color: colors.accent600,
    fontWeight: 500,
    cursor: 'pointer',
    fontSize: 'inherit',
    textDecoration: 'none',
    transition: 'color 0.15s ease',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 20,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  pageBtn: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 6,
    color: colors.stone600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    padding: '14px 20px',
    background: colors.stone800,
    color: '#fff',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    animation: 'slideUp 0.3s ease',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 20,
    width: '100%',
    maxWidth: 720,
    maxHeight: '85vh',
    overflow: 'hidden',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    animation: 'fadeIn 0.2s ease',
  },
  drawer: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  drawerContent: {
    width: '60%',
    maxWidth: 900,
    height: '100%',
    background: '#fff',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideIn 0.3s ease',
  },
};

const getStatusBadge = (status) => {
  const s = String(status || '').toLowerCase();
  let bg, color;
  if (s === 'resolved' || s === 'completed' || s === 'paid') {
    bg = colors.emerald50; color = colors.emerald700;
  } else if (s === 'in-progress' || s === 'sent' || s === 'accepted') {
    bg = colors.blue50; color = colors.blue700;
  } else if (s === 'pending' || s === 'incoming' || s === 'draft') {
    bg = colors.amber50; color = colors.amber700;
  } else if (s === 'rejected' || s === 'cancelled' || s === 'overdue') {
    bg = colors.rose50; color = colors.rose700;
  } else if (s === 'converted') {
    bg = colors.violet50; color = colors.violet600;
  } else {
    bg = colors.stone100; color = colors.stone600;
  }
  return { background: bg, color };
};

export default function CustomerDetail({ embeddedCustomerId, embeddedCustomer }) {
  const emptyAddress = { line1: '', line2: '', city: '', state: '', pincode: '' };
  const normalizeAddress = (address) => ({
    line1: String(address?.line1 || '').trim(),
    line2: String(address?.line2 || '').trim(),
    city: String(address?.city || '').trim(),
    state: String(address?.state || '').trim(),
    pincode: String(address?.pincode || '').trim(),
  });
  const params = useParams();
  const routeId = params?.id;
  const effectiveId = embeddedCustomerId ?? routeId;
  const { state } = useLocation();
  const { company } = useAuth();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(embeddedCustomer || state?.customer || null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');

  const [edit, setEdit] = useState({ name: '', email: '', phone: '', gstin: '', address: emptyAddress });
  const [saving, setSaving] = useState(false);

  // Work history (complaints)
  const [complaints, setComplaints] = useState([]);
  const [cPage, setCPage] = useState(1);
  const [cLimit, setCLimit] = useState(10);
  const [cTotal, setCTotal] = useState(0);
  const [cLoading, setCLoading] = useState(false);

  // Quotes
  const [quotes, setQuotes] = useState([]);
  const [qPage, setQPage] = useState(1);
  const [qPageSize, setQPageSize] = useState(10);
  const [qTotal, setQTotal] = useState(0);
  const [qLoading, setQLoading] = useState(false);

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [iPage, setIPage] = useState(1);
  const [iPageSize, setIPageSize] = useState(10);
  const [iTotal, setITotal] = useState(0);
  const [iLoading, setILoading] = useState(false);

  // Proformas
  const [proformas, setProformas] = useState([]);
  const [pPage, setPPage] = useState(1);
  const [pPageSize, setPPageSize] = useState(10);
  const [pTotal, setPTotal] = useState(0);
  const [pLoading, setPLoading] = useState(false);

  // Orders
  const [orders, setOrders] = useState([]);
  const [oLoading, setOLoading] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState('work');

  // Search / Filter / Sort
  const [cQuery, setCQuery] = useState('');
  const [qQuery, setQQuery] = useState('');
  const [iQuery, setIQuery] = useState('');
  const [qStatus, setQStatus] = useState('all');
  const [iStatus, setIStatus] = useState('all');
  const [qSort, setQSort] = useState('date_desc');
  const [iSort, setISort] = useState('date_desc');
  const [pQuery, setPQuery] = useState('');
  const [pStatus, setPStatus] = useState('all');
  const [pSort, setPSort] = useState('date_desc');

  const [taskDetailModal, setTaskDetailModal] = useState({ open: false, loading: false, task: null, error: '' });
  const [taskStatusMap, setTaskStatusMap] = useState({});
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', label: '', kind: '', loading: false });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!company?.id || !effectiveId || state?.customer || embeddedCustomer) return;
      try {
        setLoading(true);
        const { data } = await api.get(`/api/companies/${company.id}/customers`, { params: { page: 1, limit: 100 } });
        const found = (data?.customers || []).find(c => String(c.id) === String(effectiveId));
        if (found) {
          setCustomer(found);
          setEdit({ name: found.name || '', email: found.email || '', phone: found.phone || '', gstin: found.gstin || '', address: normalizeAddress(found.address) });
        }
      } catch (e) {
        console.error('Failed to fetch customer', e);
        setToast('Failed to load customer');
      } finally {
        setLoading(false);
      }
    };
    if (embeddedCustomer || state?.customer) {
      const c = embeddedCustomer || state.customer;
      setEdit({ name: c.name || '', email: c.email || '', phone: c.phone || '', gstin: c.gstin || '', address: normalizeAddress(c.address) });
    }
    fetchCustomer();
  }, [company?.id, effectiveId, state?.customer, embeddedCustomer]);

  const fetchOrders = async () => {
    if (!company?.id || !effectiveId) return;
    setOLoading(true);
    try {
      const res = await OrdersAPI.list({ companyId: company.id, pageSize: 200 });
      const list = Array.isArray(res?.data) ? res.data : [];
      const cid = String(effectiveId);
      const filtered = list.filter((o) => {
        const oid = o?.customer?.id != null ? String(o.customer.id) : (o?.customerId != null ? String(o.customerId) : '');
        return oid === cid;
      });
      setOrders(filtered);
    } catch (e) {
      console.error('Failed to load orders', e);
      setToast(e?.response?.data?.error || 'Failed to load orders');
    } finally { setOLoading(false); }
  };

  const fetchComplaints = async () => {
    if (!company?.id || !effectiveId) return;
    setCLoading(true);
    try {
      const params = { customerId: effectiveId, page: cPage, limit: cLimit };
      const { data } = await api.get(`/api/companies/${company.id}/complaints`, { params });
      setComplaints(data?.complaints || []);
      setCTotal(data?.totalComplaints || 0);
    } catch (e) {
      console.error('Failed to load work history', e);
    } finally { setCLoading(false); }
  };

  const fetchQuotes = async () => {
    if (!company?.id || !effectiveId) return;
    setQLoading(true);
    try {
      const params = { customerId: effectiveId, page: qPage, pageSize: qPageSize };
      const { data } = await api.get('/api/quotes', { params });
      setQuotes(data?.data || []);
      setQTotal(data?.total || 0);
    } catch (e) {
      console.error('Failed to load quotes', e);
    } finally { setQLoading(false); }
  };

  const fetchInvoices = async () => {
    if (!company?.id || !effectiveId) return;
    setILoading(true);
    try {
      const params = { customerId: effectiveId, page: iPage, pageSize: iPageSize };
      const { data } = await api.get('/api/invoices', { params });
      setInvoices(data?.data || []);
      setITotal(data?.total || 0);
    } catch (e) {
      console.error('Failed to load invoices', e);
    } finally { setILoading(false); }
  };

  const fetchProformas = async () => {
    if (!company?.id || !effectiveId) return;
    setPLoading(true);
    try {
      const params = { customerId: effectiveId, page: pPage, pageSize: pPageSize };
      const { data } = await api.get('/api/proformas', { params });
      setProformas(data?.data || []);
      setPTotal(data?.total || 0);
    } catch (e) {
      console.error('Failed to load proformas', e);
    } finally { setPLoading(false); }
  };

  useEffect(() => { fetchComplaints(); }, [company?.id, effectiveId, cPage, cLimit]);
  useEffect(() => { fetchQuotes(); }, [company?.id, effectiveId, qPage, qPageSize]);
  useEffect(() => { fetchInvoices(); }, [company?.id, effectiveId, iPage, iPageSize]);
  useEffect(() => { fetchOrders(); }, [company?.id, effectiveId]);
  useEffect(() => { fetchProformas(); }, [company?.id, effectiveId, pPage, pPageSize]);

  const visibleComplaints = useMemo(() => {
    const term = (cQuery || '').toLowerCase().trim();
    if (!term) return complaints;
    return complaints.filter(t => {
      const hay = `${t?.id ?? ''} ${t?.description ?? ''} ${t?.status ?? ''} ${t?.priority ?? ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [complaints, cQuery]);

  const sortList = (list, sortKey) => {
    const byDate = (a, b, dir) => {
      const da = Date.parse(a?.date ?? a?.createdAt ?? '') || 0;
      const db = Date.parse(b?.date ?? b?.createdAt ?? '') || 0;
      return dir === 'asc' ? da - db : db - da;
    };
    const byTotal = (a, b, dir) => {
      const ta = Number(a?.total ?? a?.totals?.total ?? 0);
      const tb = Number(b?.total ?? b?.totals?.total ?? 0);
      return dir === 'asc' ? ta - tb : tb - ta;
    };
    if (sortKey === 'date_asc') return [...list].sort((a, b) => byDate(a, b, 'asc'));
    if (sortKey === 'date_desc') return [...list].sort((a, b) => byDate(a, b, 'desc'));
    if (sortKey === 'total_asc') return [...list].sort((a, b) => byTotal(a, b, 'asc'));
    if (sortKey === 'total_desc') return [...list].sort((a, b) => byTotal(a, b, 'desc'));
    return list;
  };

  const visibleQuotes = useMemo(() => {
    const term = (qQuery || '').toLowerCase().trim();
    let list = quotes;
    if (qStatus !== 'all') list = list.filter(q => String(q?.status || '').toLowerCase() === qStatus);
    if (term) {
      list = list.filter(q => {
        const hay = `${q?.number ?? ''} ${q?.status ?? ''} ${q?.date ?? ''}`.toLowerCase();
        return hay.includes(term);
      });
    }
    return sortList(list, qSort);
  }, [quotes, qQuery, qStatus, qSort]);

  const visibleInvoices = useMemo(() => {
    const term = (iQuery || '').toLowerCase().trim();
    let list = invoices;
    if (iStatus !== 'all') list = list.filter(inv => String(inv?.status || '').toLowerCase() === iStatus);
    if (term) {
      list = list.filter(inv => {
        const hay = `${inv?.number ?? ''} ${inv?.status ?? ''} ${inv?.date ?? ''}`.toLowerCase();
        return hay.includes(term);
      });
    }
    return sortList(list, iSort);
  }, [invoices, iQuery, iStatus, iSort]);

  const visibleProformas = useMemo(() => {
    const term = (pQuery || '').toLowerCase().trim();
    let list = proformas;
    if (pStatus !== 'all') list = list.filter(p => String(p?.status || '').toLowerCase() === pStatus);
    if (term) {
      list = list.filter(p => {
        const hay = `${p?.number ?? ''} ${p?.status ?? ''} ${p?.date ?? ''}`.toLowerCase();
        return hay.includes(term);
      });
    }
    return sortList(list, pSort);
  }, [proformas, pQuery, pStatus, pSort]);

  useEffect(() => {
    const allIds = (orders || []).flatMap(o => Array.isArray(o.taskIds) ? o.taskIds : []);
    const missing = allIds.filter((id) => taskStatusMap[id] == null);
    if (missing.length === 0) return;
    const fetchAll = async () => {
      try {
        const results = await Promise.all(missing.map((id) => api.get(`/api/tasks/${id}`).catch(() => null)));
        const next = { ...taskStatusMap };
        results.forEach((resp, idx) => {
          const id = missing[idx];
          const data = resp?.data;
          const task = data?.task || data;
          if (task && task.status) {
            next[id] = task.status;
          }
        });
        setTaskStatusMap(next);
      } catch (_) {}
    };
    fetchAll();
  }, [orders]);

  const openTaskDetail = async (taskId) => {
    try {
      setTaskDetailModal({ open: true, loading: true, task: null, error: '' });
      const { data } = await api.get(`/api/tasks/${taskId}`);
      if (data?.is_success === false && data?.message) {
        setTaskDetailModal({ open: true, loading: false, task: null, error: data.message });
        return;
      }
      const task = data?.task || data;
      setTaskDetailModal({ open: true, loading: false, task, error: '' });
      if (task?.id && task?.status) {
        setTaskStatusMap((m) => ({ ...m, [task.id]: task.status }));
      }
    } catch (e) {
      setTaskDetailModal({ open: true, loading: false, task: null, error: e?.response?.data?.message || e?.message || 'Failed to load task' });
    }
  };
  const closeTaskDetail = () => { setTaskDetailModal({ open: false, loading: false, task: null, error: '' }); };

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value ?? ''));
      setToast('Copied to clipboard');
    } catch (_) {}
  };

  const openOrderDetail = (orderId) => { if (orderId) navigate(`/orders/${orderId}`); };

  const handleDownloadPdf = async (pdfId) => {
    try {
      setPdfPreview({ open: true, url: '', label: '', kind: 'Invoice', loading: true });
      const { data } = await api.get(`/api/invoices/${pdfId}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL returned');
      setPdfPreview((p) => ({ ...p, url, loading: false }));
    } catch (e) {
      console.error('Failed to load PDF', e);
      setPdfPreview({ open: false, url: '', label: '', kind: 'Invoice', loading: false });
      setToast('Failed to load PDF');
    }
  };

  const handleSendEmail = async (eid) => {
    try {
      await api.post(`/api/invoices/${eid}/send`);
      setToast('Email sent successfully');
    } catch (e) {
      console.error(e);
      setToast('Failed to send email');
    }
  };

  const handleSave = async () => {
    if (!company?.id || !effectiveId) return;
    try {
      setSaving(true);
      const body = {
        name: (edit.name || '').trim(),
        email: (edit.email || '').trim(),
        phone: (edit.phone || '').trim(),
        gstin: (edit.gstin || '').trim(),
        address: normalizeAddress(edit.address),
      };
      const { data } = await api.put(`/api/companies/${company.id}/customers/${effectiveId}`, body);
      setCustomer(data?.customer || { ...customer, ...body });
      setToast('Customer updated successfully');
    } catch (e) {
      console.error('Update failed', e);
      setToast(e?.response?.data?.message || 'Failed to update');
    } finally { setSaving(false); }
  };

  const formatINR = (val) => {
    try { 
      const num = Number(val || 0);
      return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(num);
    } catch { return val; }
  };

  const handleViewInvoice = async (invId) => {
    try {
      setPdfPreview({ open: true, url: '', label: '', kind: 'Invoice', loading: true });
      const { data } = await api.get(`/api/invoices/${invId}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL');
      setPdfPreview((p) => ({ ...p, url, loading: false }));
    } catch (e) { 
      console.error('Failed to load invoice PDF', e);
      setPdfPreview({ open: false, url: '', label: '', kind: 'Invoice', loading: false });
      setToast('Failed to load PDF');
    }
  };

  const handleViewQuote = async (quoteId) => {
    try {
      setPdfPreview({ open: true, url: '', label: '', kind: 'Quote', loading: true });
      const { data } = await api.get(`/api/quotes/${quoteId}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL');
      setPdfPreview((p) => ({ ...p, url, loading: false }));
    } catch (e) { 
      console.error('Failed to load quote PDF', e);
      setPdfPreview({ open: false, url: '', label: '', kind: 'Quote', loading: false });
      setToast('Failed to load PDF');
    }
  };

  const handleViewProforma = async (id) => {
    try {
      setPdfPreview({ open: true, url: '', label: '', kind: 'Proforma', loading: true });
      const { data } = await api.get(`/api/proformas/${id}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL');
      setPdfPreview((p) => ({ ...p, url, loading: false }));
    } catch (e) { 
      console.error('Failed to load proforma PDF', e);
      setPdfPreview({ open: false, url: '', label: '', kind: 'Proforma', loading: false });
      setToast('Failed to load PDF');
    }
  };

  const handleSendProforma = async (id) => {
    try {
      await api.post(`/api/proformas/${id}/send`);
      setToast('Email sent successfully');
    } catch (e) { setToast('Failed to send email'); }
  };

  const tabs = [
    { id: 'work', label: 'Work History', icon: FiClipboard },
    { id: 'orders', label: 'Orders', icon: FiPackage },
    { id: 'quotes', label: 'Quotes', icon: FiFileText },
    { id: 'invoices', label: 'Invoices', icon: FiFileText },
    { id: 'proformas', label: 'Proformas', icon: FiFileText },
  ];

  const renderPagination = (page, setPage, pageSize, setPageSize, total) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '12px 20px' }}>
      <span style={{ fontSize: 12, color: colors.stone500 }}>
        Showing <strong style={{ color: colors.stone700 }}>{Math.min((page - 1) * pageSize + 1, total)}-{Math.min(page * pageSize, total)}</strong> of <strong style={{ color: colors.stone700 }}>{total}</strong>
      </span>
      <div style={styles.pagination}>
        <button
          style={{ ...styles.pageBtn, opacity: page <= 1 ? 0.5 : 1 }}
          disabled={page <= 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
        >
          <FiChevronLeft size={18} />
        </button>
        <span style={{ padding: '0 12px', fontSize: 12, color: colors.stone600 }}>Page {page}</span>
        <button
          style={{ ...styles.pageBtn, opacity: page * pageSize >= total ? 0.5 : 1 }}
          disabled={page * pageSize >= total}
          onClick={() => setPage(p => p + 1)}
        >
          <FiChevronRight size={18} />
        </button>
        <select
          style={{ ...styles.select, marginLeft: 8 }}
          value={pageSize}
          onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
        >
          <option value={10}>10 / page</option>
          <option value={20}>20 / page</option>
          <option value={50}>50 / page</option>
        </select>
      </div>
    </div>
  );

  const renderEmptyState = (message) => (
    <tr>
      <td colSpan={10} style={{ padding: '48px 16px', textAlign: 'center' }}>
        <div style={{ color: colors.stone400, fontSize: 14 }}>{message}</div>
      </td>
    </tr>
  );

  const renderLoading = () => (
    <tr>
      <td colSpan={10} style={{ padding: '48px 16px', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </td>
    </tr>
  );

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{customer?.name || 'Customer'}</h1>
          <p style={styles.subtitle}>Customer Profile</p>
        </div>
      </div>

      {/* Toast */}
      {!!toast && (
        <div style={styles.toast}>
          <span>{toast}</span>
          <button onClick={() => setToast('')} style={{ ...styles.btnIcon, color: '#fff' }}>
            <FiX size={16} />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ ...styles.card, padding: 48, textAlign: 'center' }}>
          <div style={{ display: 'inline-block', width: 32, height: 32, border: `3px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}

      {/* Customer Info Card */}
      {customer && (
        <div style={styles.card}>
          <div style={styles.cardBody}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20, marginBottom: 20 }}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Name</label>
                <input
                  style={styles.input}
                  value={edit.name}
                  onChange={(e) => setEdit(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Customer name"
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Email</label>
                <input
                  style={styles.input}
                  type="email"
                  value={edit.email}
                  onChange={(e) => setEdit(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  style={styles.input}
                  value={edit.phone}
                  onChange={(e) => setEdit(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+91 00000 00000"
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>GSTIN</label>
                <input
                  style={styles.input}
                  value={edit.gstin}
                  onChange={(e) => setEdit(prev => ({ ...prev, gstin: e.target.value }))}
                  placeholder="GSTIN number"
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Address Line 1</label>
                <input
                  style={styles.input}
                  value={edit.address?.line1 || ''}
                  onChange={(e) => setEdit(prev => ({ ...prev, address: { ...(prev.address || emptyAddress), line1: e.target.value } }))}
                  placeholder="Street address"
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Address Line 2</label>
                <input
                  style={styles.input}
                  value={edit.address?.line2 || ''}
                  onChange={(e) => setEdit(prev => ({ ...prev, address: { ...(prev.address || emptyAddress), line2: e.target.value } }))}
                  placeholder="Apartment, suite, etc."
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>City</label>
                <input
                  style={styles.input}
                  value={edit.address?.city || ''}
                  onChange={(e) => setEdit(prev => ({ ...prev, address: { ...(prev.address || emptyAddress), city: e.target.value } }))}
                  placeholder="City"
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>State</label>
                <input
                  style={styles.input}
                  value={edit.address?.state || ''}
                  onChange={(e) => setEdit(prev => ({ ...prev, address: { ...(prev.address || emptyAddress), state: e.target.value } }))}
                  placeholder="State"
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Pincode</label>
                <input
                  style={styles.input}
                  value={edit.address?.pincode || ''}
                  onChange={(e) => setEdit(prev => ({ ...prev, address: { ...(prev.address || emptyAddress), pincode: e.target.value } }))}
                  placeholder="Pincode"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              {(edit?.email || '').trim() && (
                <a href={`mailto:${edit.email.trim()}`} style={{ ...styles.btnSecondary, textDecoration: 'none' }}>
                  <FiMail size={16} /> Email
                </a>
              )}
              {(edit?.phone || '').trim() && (
                <a href={`tel:${edit.phone.trim()}`} style={{ ...styles.btnSecondary, textDecoration: 'none' }}>
                  <FiPhone size={16} /> Call
                </a>
              )}
              {(edit?.gstin || '').trim() && (
                <button style={styles.btnSecondary} onClick={() => copyToClipboard(edit.gstin)}>
                  <FiCopy size={16} /> Copy GSTIN
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* PDF Preview Drawer */}
      {pdfPreview.open && (
        <div style={styles.drawer} onClick={() => setPdfPreview({ open: false, url: '', label: '', kind: '', loading: false })}>
          <div style={styles.drawerContent} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 24px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.stone800 }}>
                {pdfPreview.kind || 'Document'} Preview
              </h3>
              <div style={{ display: 'flex', gap: 10 }}>
                {!!pdfPreview.url && (
                  <a href={pdfPreview.url} target="_blank" rel="noreferrer" style={{ ...styles.btnSecondary, textDecoration: 'none' }}>
                    <FiExternalLink size={16} /> Open in New Tab
                  </a>
                )}
                <button style={styles.btnSecondary} onClick={() => setPdfPreview({ open: false, url: '', label: '', kind: '', loading: false })}>
                  Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, position: 'relative', background: colors.stone100 }}>
              {pdfPreview.loading ? (
                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ display: 'inline-block', width: 32, height: 32, border: `3px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              ) : pdfPreview.url ? (
                <iframe title="PDF" src={pdfPreview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : (
                <div style={{ padding: 24, color: colors.stone500 }}>No preview available</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Work History Tab */}
      {activeTab === 'work' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.searchWrapper}>
              <FiSearch size={16} style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Search tasks..."
                value={cQuery}
                onChange={(e) => setCQuery(e.target.value)}
              />
            </div>
            <span style={{ fontSize: 12, color: colors.stone500 }}>
              {visibleComplaints.length} of {cTotal} tasks
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={{ ...styles.th, minWidth: 280 }}>Description</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Department</th>
                  <th style={styles.th}>Created</th>
                </tr>
              </thead>
              <tbody>
                {cLoading && renderLoading()}
                {!cLoading && visibleComplaints.length === 0 && renderEmptyState('No work history found')}
                {!cLoading && visibleComplaints.map(t => (
                  <tr key={t.id} style={{ transition: 'background 0.15s ease' }} onMouseEnter={e => e.currentTarget.style.background = colors.stone50} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={styles.td}>
                      <button style={styles.linkBtn} onClick={() => openTaskDetail(t.id)}>
                        #{t.id}
                      </button>
                    </td>
                    <td style={styles.td}>
                      <span style={{ display: 'block', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.description || ''}>
                        {t.description || '-'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...getStatusBadge(t.status) }}>
                        {String(t.status || '').toUpperCase()}
                      </span>
                    </td>
                    <td style={styles.td}>{t.priority || '-'}</td>
                    <td style={styles.td}>{t.department?.name || '-'}</td>
                    <td style={styles.td}>{(t.createdAt || '').toString().slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination(cPage, setCPage, cLimit, setCLimit, cTotal)}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Orders</h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Quote</th>
                  <th style={styles.th}>Invoice</th>
                  <th style={styles.th}>Tasks</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {oLoading && renderLoading()}
                {!oLoading && orders.length === 0 && renderEmptyState('No orders found')}
                {!oLoading && orders.map(o => (
                  <tr key={o.id} style={{ transition: 'background 0.15s ease' }} onMouseEnter={e => e.currentTarget.style.background = colors.stone50} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={styles.td}>
                      <button style={styles.linkBtn} onClick={() => openOrderDetail(o.id)}>
                        {o.number || `ORD${o.id}`}
                      </button>
                    </td>
                    <td style={styles.td}>
                      {o?.quote?.id || o?.quoteId ? (
                        <button style={styles.linkBtn} onClick={() => handleViewQuote(o?.quote?.id || o?.quoteId)}>
                          {o?.quote?.number || `QUOTE-${o?.quote?.id || o?.quoteId}`}
                        </button>
                      ) : '-'}
                    </td>
                    <td style={styles.td}>
                      {o.invoiceId ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button style={styles.linkBtn} onClick={() => handleDownloadPdf(o.invoiceId)}>
                            {o?.invoice?.number || `INV${String(o.invoiceId)}`}
                          </button>
                          <button style={styles.btnIcon} onClick={() => handleSendEmail(o.invoiceId)} title="Send email">
                            <FiMail size={14} />
                          </button>
                        </div>
                      ) : '-'}
                    </td>
                    <td style={styles.td}>
                      {Array.isArray(o.taskIds) && o.taskIds.length > 0 ? (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {o.taskIds.map((tid) => {
                            const status = taskStatusMap[tid];
                            const badgeStyle = getStatusBadge(status);
                            return (
                              <button
                                key={tid}
                                style={{ ...styles.linkBtn, color: badgeStyle.color }}
                                onClick={() => openTaskDetail(tid)}
                                title={status ? `Status: ${status}` : 'View task'}
                              >
                                {tid}
                              </button>
                            );
                          })}
                        </div>
                      ) : '-'}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...getStatusBadge(o.status) }}>
                        {String(o.status || '').toUpperCase()}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                      {formatINR(o?.totals?.total || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quotes Tab */}
      {activeTab === 'quotes' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Quotes</h3>
            <Link
              to={`/quote/new?customerId=${effectiveId}`}
              style={{ ...styles.btnPrimary, textDecoration: 'none' }}
            >
              + New Quote
            </Link>
          </div>
          <div style={{ padding: '12px 20px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={styles.searchWrapper}>
              <FiSearch size={16} style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Search quotes..."
                value={qQuery}
                onChange={(e) => setQQuery(e.target.value)}
              />
            </div>
            <select style={styles.select} value={qStatus} onChange={(e) => setQStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="converted">Converted</option>
            </select>
            <select style={styles.select} value={qSort} onChange={(e) => setQSort(e.target.value)}>
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="total_desc">Highest Total</option>
              <option value="total_asc">Lowest Total</option>
            </select>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Number</th>
                  <th style={styles.th}>Date</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                  <th style={styles.th}>Source</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Proformas</th>
                </tr>
              </thead>
              <tbody>
                {qLoading && renderLoading()}
                {!qLoading && visibleQuotes.length === 0 && renderEmptyState('No quotes found')}
                {!qLoading && visibleQuotes.map(q => (
                  <tr key={q.id} style={{ transition: 'background 0.15s ease' }} onMouseEnter={e => e.currentTarget.style.background = colors.stone50} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={styles.td}>
                      <button style={{ ...styles.linkBtn, fontWeight: 600 }} onClick={() => handleViewQuote(q.id)}>
                        {q.number}
                      </button>
                    </td>
                    <td style={styles.td}>{q.date}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                      {formatINR(q.total)}
                    </td>
                    <td style={styles.td}>
                      <span style={{ color: colors.stone500 }}>
                        {q.source ? String(q.source).replace(/_/g, ' ') : (q.recurringInvoiceId ? 'RECURRING' : 'DIRECT')}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...getStatusBadge(q.status) }}>
                        {String(q.status || '').toUpperCase()}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {Array.isArray(q.proformas) && q.proformas.length > 0 ? (
                          q.proformas.map((p) => (
                            <button
                              key={p.id}
                              style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px', borderRadius: 16 }}
                              onClick={() => handleViewProforma(p.id)}
                              title={`${p.number || ''}${p.paymentDate ? ` • ${String(p.paymentDate).slice(0, 10)}` : ''}`}
                            >
                              {formatINR(p.amountPaid || p.total || 0)}
                            </button>
                          ))
                        ) : (
                          <span style={{ color: colors.stone400 }}>-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination(qPage, setQPage, qPageSize, setQPageSize, qTotal)}
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Invoices</h3>
          </div>
          <div style={{ padding: '12px 20px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={styles.searchWrapper}>
              <FiSearch size={16} style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Search invoices..."
                value={iQuery}
                onChange={(e) => setIQuery(e.target.value)}
              />
            </div>
            <select style={styles.select} value={iStatus} onChange={(e) => setIStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select style={styles.select} value={iSort} onChange={(e) => setISort(e.target.value)}>
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="total_desc">Highest Total</option>
              <option value="total_asc">Lowest Total</option>
            </select>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Number</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Due Date</th>
                  <th style={styles.th}>Created From</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {iLoading && renderLoading()}
                {!iLoading && visibleInvoices.length === 0 && renderEmptyState('No invoices found')}
                {!iLoading && visibleInvoices.map(inv => (
                  <tr key={inv.id} style={{ transition: 'background 0.15s ease' }} onMouseEnter={e => e.currentTarget.style.background = colors.stone50} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={styles.td}>
                      <button style={{ ...styles.linkBtn, fontWeight: 600 }} onClick={() => handleViewInvoice(inv.id)}>
                        {inv.number}
                      </button>
                    </td>
                    <td style={styles.td}>{inv.date}</td>
                    <td style={styles.td}>{inv.dueDate || '-'}</td>
                    <td style={styles.td}>
                      {(() => {
                        const from = inv.createdFrom ? String(inv.createdFrom).replace(/_/g, ' ') : '-';
                        const quotes = Array.isArray(inv.sourceQuotes) ? inv.sourceQuotes : [];
                        if (from.includes('CONVERTED QUOTE') && quotes.length) {
                          return (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {quotes.map((q) => (
                                <button
                                  key={q.id}
                                  style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px', borderRadius: 16 }}
                                  onClick={() => handleViewQuote(q.id)}
                                >
                                  {q.number || q.id}
                                </button>
                              ))}
                            </div>
                          );
                        }
                        return <span style={{ color: colors.stone500 }}>{from}</span>;
                      })()}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                      {formatINR(inv.total)}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...getStatusBadge(inv.status) }}>
                        {String(inv.status || '').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination(iPage, setIPage, iPageSize, setIPageSize, iTotal)}
        </div>
      )}

      {/* Proformas Tab */}
      {activeTab === 'proformas' && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Proformas</h3>
          </div>
          <div style={{ padding: '12px 20px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={styles.searchWrapper}>
              <FiSearch size={16} style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Search proformas..."
                value={pQuery}
                onChange={(e) => setPQuery(e.target.value)}
              />
            </div>
            <select style={styles.select} value={pStatus} onChange={(e) => setPStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select style={styles.select} value={pSort} onChange={(e) => setPSort(e.target.value)}>
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="total_desc">Highest Total</option>
              <option value="total_asc">Lowest Total</option>
            </select>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Number</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Source</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pLoading && renderLoading()}
                {!pLoading && visibleProformas.length === 0 && renderEmptyState('No proformas found')}
                {!pLoading && visibleProformas.map(p => (
                  <tr key={p.id} style={{ transition: 'background 0.15s ease' }} onMouseEnter={e => e.currentTarget.style.background = colors.stone50} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={styles.td}>
                      <button style={{ ...styles.linkBtn, fontWeight: 600 }} onClick={() => handleViewProforma(p.id)}>
                        {p.number}
                      </button>
                    </td>
                    <td style={styles.td}>{p.date}</td>
                    <td style={styles.td}>
                      {p?.recurringInvoiceId ? (
                        <span style={{ color: colors.stone500 }}>Recurring</span>
                      ) : p?.quoteId ? (
                        <button style={styles.linkBtn} onClick={() => handleViewQuote(p.quoteId)}>
                          {p?.quoteNumber ? `Quote ${p.quoteNumber}` : `Quote ${p.quoteId}`}
                        </button>
                      ) : (
                        <span style={{ color: colors.stone400 }}>-</span>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                      {formatINR(p.total || 0)}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...getStatusBadge(p.status) }}>
                        {String(p.status || '').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination(pPage, setPPage, pPageSize, setPPageSize, pTotal)}
        </div>
      )}

      {/* Task Detail Modal (shared Tasks view) */}
      {taskDetailModal.open && taskDetailModal.loading && (
        <div style={styles.modal} onClick={closeTaskDetail}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ display: 'inline-block', width: 32, height: 32, border: `3px solid ${colors.stone200}`, borderTopColor: colors.stone700, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <div style={{ marginTop: 12, color: colors.stone600, fontSize: 13 }}>Loading task…</div>
            </div>
          </div>
        </div>
      )}
      {taskDetailModal.open && !!taskDetailModal.error && !taskDetailModal.loading && (
        <div style={styles.modal} onClick={closeTaskDetail}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: 20, borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.stone800 }}>Task</h3>
              <button style={styles.btnIcon} onClick={closeTaskDetail} aria-label="Close task modal">
                <FiX size={20} />
              </button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ padding: 14, background: colors.rose50, color: colors.rose700, borderRadius: 12, fontSize: 14 }}>
                {taskDetailModal.error}
              </div>
            </div>
          </div>
        </div>
      )}
      <TaskDetailsModal
        isOpen={taskDetailModal.open && !taskDetailModal.loading && !!taskDetailModal.task && !taskDetailModal.error}
        task={taskDetailModal.task}
        onClose={closeTaskDetail}
        // View-only in CustomerDetail; Tasks page owns actions/editing/subtasks.
        canModifyTask={() => false}
      />
    </div>
  );
}
